import { useState, useRef, useEffect } from "react";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const digits = (s) => (s || "").replace(/\D/g, "");

export const ClientSearchInput = ({ clients, value, onChange, placeholder = "Digite o nome do cliente...", testId = "client-search-input", className = "", variant = "dark", emptyText = "Nenhum cliente encontrado" }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = clients.find((c) => c.id === value);
  const light = variant === "light";
  const inputCls = light
    ? "w-full bg-white border border-slate-300 rounded pl-9 pr-8 py-2.5 sm:py-1.5 text-base sm:text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
    : "w-full bg-zinc-900 border border-zinc-800 rounded pl-9 pr-8 py-2.5 sm:py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";
  const dropCls = light
    ? "absolute z-50 mt-1 left-0 min-w-full w-max max-w-[min(24rem,88vw)] bg-white border border-slate-300 rounded-lg shadow-xl max-h-64 overflow-y-auto"
    : "absolute z-50 mt-1 left-0 min-w-full w-max max-w-[min(24rem,88vw)] bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl max-h-64 overflow-y-auto";
  const optCls = light
    ? "w-full text-left px-3 py-3 sm:py-2.5 text-base sm:text-sm text-slate-800 hover:bg-blue-50 transition-colors"
    : "w-full text-left px-3 py-3 sm:py-2.5 text-base sm:text-sm text-zinc-300 hover:bg-indigo-600/20 hover:text-white transition-colors";

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const matches = query.trim()
    ? clients.filter((c) => norm(c.name).includes(norm(query)) || (digits(query) && digits(c.document).includes(digits(query)))).slice(0, 8)
    : clients.slice(0, 8);

  return (
    <div ref={ref} className={`relative ${open && !selected ? "z-40" : ""} ${className}`}>
      <div className="relative">
        <MagnifyingGlass size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${light ? "text-slate-400" : "text-zinc-500"}`} />
        <input
          data-testid={testId}
          className={inputCls}
          placeholder={placeholder}
          value={selected ? selected.name : query}
          onChange={(e) => {
            let v = e.target.value;
            if (selected) {
              onChange("");
              v = v.startsWith(selected.name) ? v.slice(selected.name.length) : v;
            }
            setQuery(v);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
        />
        {(selected || query) && (
          <button type="button" data-testid={`${testId}-clear`} aria-label="Limpar"
            onClick={() => { onChange(""); setQuery(""); setOpen(false); }}
            className={`absolute right-2 top-1/2 -translate-y-1/2 p-0.5 transition-colors ${light ? "text-slate-400 hover:text-slate-700" : "text-zinc-500 hover:text-zinc-200"}`}>
            <X size={14} />
          </button>
        )}
      </div>
      {open && !selected && (
        <div data-testid={`${testId}-options`} className={dropCls}>
          {matches.length === 0 ? (
            <p className={`px-3 py-2.5 text-sm ${light ? "text-slate-400" : "text-zinc-600"}`}>{emptyText}</p>
          ) : (
            matches.map((c) => (
              <button key={c.id} type="button" data-testid={`${testId}-option-${c.code ?? c.id}`}
                onClick={() => { onChange(c.id); setQuery(""); setOpen(false); }}
                className={optCls}>
                {c.name}
                {c.document && <span className={`block text-xs ${light ? "text-slate-400" : "text-zinc-600"}`}>{c.document}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
